import axios, { AxiosRequestConfig, CancelTokenSource } from 'axios';

import httpClient from '@/config/http.client';
import { CancelPrevRequestKey, CancelRequestKey } from '@/config/http';

import { ApiConfig, HttpMethod, RuntimeConfig, DynamicUrl, StaticUrl, CANCEL_PREV_REQUEST_DEFAULT } from './constant';
import { convertURLSearchParams } from './util';
import { SseRuntimeConfig } from './hooks/use-event-source';

const cancelTokenSourceMap: Map<ApiConfig<any>, CancelTokenSource> = new Map();

function cancelRequest(key: ApiConfig<any>, message?: string) {
  let cancelTokenSource = cancelTokenSourceMap.get(key);
  setTimeout(() => {
    cancelTokenSource?.cancel(message);
  });
}

function createCancelToken(key: ApiConfig<any>) {
  const cancelTokenSource = axios.CancelToken.source();
  cancelTokenSourceMap.set(key, cancelTokenSource);
  return cancelTokenSource.token;
}

function removeCancelToken(key: ApiConfig<any>) {
  cancelTokenSourceMap.delete(key);
}

export function convertArgs(config: { url: StaticUrl | DynamicUrl }, arg_0?: any, arg_1?: any, arg_2?: any) {
  let url: string;
  if (typeof config.url === 'string') url = config.url;
  else if (typeof arg_0 === 'string' || typeof arg_0 === 'number') {
    url = config.url(arg_0);
  } else {
    throw new Error(`The urlArg parameter is missing.`);
  }
  let payload = typeof config.url === 'string' ? arg_0 : arg_1;
  let runtimeConfig = typeof config.url === 'string' ? arg_1 : arg_2;
  if (payload instanceof RuntimeConfig || payload instanceof SseRuntimeConfig) {
    runtimeConfig = payload;
    payload = undefined;
  } else if (!(runtimeConfig instanceof RuntimeConfig || runtimeConfig instanceof SseRuntimeConfig)) {
    runtimeConfig = undefined;
  }
  return { url, payload, runtimeConfig };
}

interface Request {
  <T>(config: ApiConfig<StaticUrl>): Promise<T | null>;
  <T>(config: ApiConfig<StaticUrl>, runtimeConfig?: RuntimeConfig<any>): Promise<T | null>;
  <T, P>(config: ApiConfig<StaticUrl>, payload: P): Promise<T | null>;
  <T, P>(config: ApiConfig<StaticUrl>, payload: P, runtimeConfig?: RuntimeConfig<P>): Promise<T | null>;
  <T>(config: ApiConfig<DynamicUrl>, urlArg: string | number): Promise<T | null>;
  <T>(config: ApiConfig<DynamicUrl>, urlArg: string | number, runtimeConfig?: RuntimeConfig<any>): Promise<T | null>;
  <T, P>(config: ApiConfig<DynamicUrl>, urlArg: string | number, payload: P): Promise<T | null>;
  <T, P>(
    config: ApiConfig<DynamicUrl>,
    urlArg: string | number,
    payload: P,
    runtimeConfig?: RuntimeConfig<P>,
  ): Promise<T | null>;
}

export const request: Request = async (
  config: ApiConfig<StaticUrl | DynamicUrl>,
  arg_0?: any,
  arg_1?: any,
  arg_2?: any,
) => {
  const { url, payload, runtimeConfig } = convertArgs(config, arg_0, arg_1, arg_2);

  const axiosRequestConfig: AxiosRequestConfig<any> = {
    url,
    method: config.method || HttpMethod.GET,
    ignoreError: config.ignoreError,
    headers: config.headers,
    withCredentials: config.withCredentials,
  };

  if (runtimeConfig) {
    Object.keys(runtimeConfig).forEach((k) => {
      if (runtimeConfig[k] !== undefined) axiosRequestConfig[k as keyof AxiosRequestConfig<any>] = runtimeConfig[k];
    });
  }

  if (axiosRequestConfig.method === HttpMethod.GET) {
    if (payload) axiosRequestConfig.params = convertURLSearchParams(payload);
  } else {
    axiosRequestConfig.data = payload;
  }

  const cancelPrev = config.cancelPrev === undefined ? CANCEL_PREV_REQUEST_DEFAULT : config.cancelPrev;
  if (cancelPrev) cancelRequest(config, CancelPrevRequestKey);
  axiosRequestConfig.cancelToken = createCancelToken(config);

  const res = await (config.axiosInstance || httpClient.instance).request(axiosRequestConfig);
  removeCancelToken(config);
  return res.data;
};

export function cancel(config: ApiConfig<any>) {
  cancelRequest(config, CancelRequestKey);
  removeCancelToken(config);
}

export function cancelAll() {
  cancelTokenSourceMap.forEach((cancelTokenSource, config) => {
    cancelTokenSource.cancel(CancelRequestKey);
    removeCancelToken(config);
  });
}
